// Client half of subcontractor payments (Session ARZ): the Xero payable-contact settings, and (Item 2) the payment
// details rules the public forms check before they post. The SERVER is the authority on every rule here; this file
// exists so a form can say what is wrong before a round trip, and a parity test
// (server/src/modules/subcontractors/subcontractorPaymentsParity.test.ts) fails if the two drift.

// ---- Xero payable contact settings (mirror of server/src/modules/settings/subcontractorXeroSettings.ts) ----
export const XERO_BILL_PAYMENT_TERM_TYPES = ["DAYSAFTERBILLDATE", "DAYSAFTERBILLMONTH", "OFCURRENTMONTH", "OFFOLLOWINGMONTH"] as const;
export type XeroBillPaymentTermTypeKey = (typeof XERO_BILL_PAYMENT_TERM_TYPES)[number];

export const BILL_PAYMENT_TERM_LABELS: Record<XeroBillPaymentTermTypeKey, string> = {
  DAYSAFTERBILLDATE: "Days after the bill date",
  DAYSAFTERBILLMONTH: "Days after the end of the bill month",
  OFCURRENTMONTH: "Day of the month the bill is dated",
  OFFOLLOWINGMONTH: "Day of the month after the bill",
};

export function isDayOfMonthTermType(type: XeroBillPaymentTermTypeKey): boolean {
  return type === "OFCURRENTMONTH" || type === "OFFOLLOWINGMONTH";
}

export interface SubcontractorXeroSettings {
  purchasesAccountCode: string | null;
  billPaymentDays: number | null;
  billPaymentTermType: XeroBillPaymentTermTypeKey | null;
  billPaymentTermsText: string | null;
}

export interface SubcontractorXeroSettingsInput {
  purchasesAccountCode: string | null;
  billPaymentDays: number | null;
  billPaymentTermType: XeroBillPaymentTermTypeKey | null;
}

// ---- Payment details (mirror of server/src/modules/subcontractors/bankDetails.ts, Item 2) ----
// The same rules and the SAME messages as the server, so a form never says one thing and the server another. Nothing
// here stores, logs or sends anything; the numbers leave the page only in the form's own POST.
export const BANK_ACCOUNT_NAME_MAX = 100;

export interface PaymentDetailsForm {
  accountName: string;
  sortCode: string;
  accountNumber: string;
  accountNumberConfirm: string;
}

export const EMPTY_PAYMENT_DETAILS: PaymentDetailsForm = { accountName: "", sortCode: "", accountNumber: "", accountNumberConfirm: "" };

export type PaymentDetailsField = keyof PaymentDetailsForm;

// The same separators the server ignores (whitespace, hyphens, the Unicode hyphens and dashes, the minus sign).
const DIGIT_SEPARATORS = /[\s\-\u2010-\u2015\u2212]/g;

export function normaliseSortCode(raw: string): string | null {
  const digits = raw.replace(DIGIT_SEPARATORS, "");
  return /^\d{6}$/.test(digits) ? digits : null;
}

export function normaliseAccountNumber(raw: string): string | null {
  const digits = raw.replace(DIGIT_SEPARATORS, "");
  return /^\d{8}$/.test(digits) ? digits : null;
}

// Returns field -> message for every problem (the form marks each field), in the server's wording.
export function paymentDetailsErrors(f: PaymentDetailsForm): Partial<Record<PaymentDetailsField, string>> {
  const e: Partial<Record<PaymentDetailsField, string>> = {};
  const name = f.accountName.trim();
  if (!name) e.accountName = "Please enter the name on the bank account.";
  else if (name.length > BANK_ACCOUNT_NAME_MAX)
    e.accountName = `Please enter the account name exactly as your bank shows it, in no more than ${BANK_ACCOUNT_NAME_MAX} characters.`;
  if (!normaliseSortCode(f.sortCode)) e.sortCode = "Please enter your sort code as 6 digits, for example 12-34-56.";
  const account = normaliseAccountNumber(f.accountNumber);
  if (!account)
    e.accountNumber = "Please enter your account number as 8 digits. If your bank gave you a number of a different length, please call us on 0203 576 4760.";
  else if (normaliseAccountNumber(f.accountNumberConfirm) !== account)
    e.accountNumberConfirm = "The two account numbers do not match. Please check them and enter them again.";
  return e;
}

// ---- The office panel (mirror of server/src/modules/subcontractors/paymentDetailsView.ts, Item 3) ----
// Masks, stamps and derived states only: the one place full numbers reach the browser is the admin reveal.
export type PaymentDetailsState = "none" | "changed_unverified" | "unverified" | "verified_not_in_xero" | "ready";
export type XeroBankState = "no_contact" | "nothing_to_send" | "not_sent" | "sent" | "old_details_in_xero";

export interface PaymentDetailsCurrentView {
  version: string;
  accountName: string;
  sortCodeMasked: string;
  accountNumberMasked: string;
  source: "onboarding_link" | "office_entry";
  sourceLabel: string;
  sourceNote: string | null;
  submittedAt: string;
  submittedByName: string | null;
  submittedByUserId: string | null;
  linkRequestedByUserId: string | null;
  linkRequestedByName: string | null;
  changedFromPrevious: boolean;
  verifiedAt: string | null;
  verifiedByName: string | null;
  verificationNote: string | null;
  pushedToXeroAt: string | null;
  phoneArrivedWithDetails: boolean;
}

export interface PaymentDetailsHistoryView {
  accountName: string;
  sortCodeMasked: string | null;
  accountNumberMasked: string | null;
  source: "onboarding_link" | "office_entry";
  submittedAt: string;
  verifiedAt: string | null;
  pushedToXeroAt: string | null;
  endedAt: string;
  endedReason: "replaced" | "cleared";
}

export interface PaymentDetailsView {
  storageAvailable: boolean;
  state: PaymentDetailsState;
  current: PaymentDetailsCurrentView | null;
  previousVerified: { accountName: string; sortCodeMasked: string | null; accountNumberMasked: string | null; verifiedAt: string | null; pushedToXeroAt: string | null } | null;
  xero: { linked: boolean; state: XeroBankState; lastSentAt: string | null };
  request: { active: boolean; requestedAt: string | null; expiresAt: string | null };
  contactPhone: string | null;
  contactPhoneFromRegistrationAt: string | null;
  history: PaymentDetailsHistoryView[];
}

export interface RevealedPaymentDetails {
  accountName: string;
  sortCode: string;
  accountNumber: string;
  visibleForSeconds: number;
}

export interface OfficePaymentDetailsEntry extends PaymentDetailsForm {
  sourceNote: string;
}

export interface OfficePaymentDetailsEntryResult {
  unchanged: boolean;
  changed?: boolean;
  alertSent?: boolean | null;
  view: PaymentDetailsView;
}

// Session RC2: the request is a Gmail DRAFT the office sends. url is the link only when no draft was made.
export interface PaymentDetailsRequestResult {
  drafted: boolean;
  draftError: string | null;
  expiresAt: string;
  url: string | null;
  earlierDraftNote: string;
  view: PaymentDetailsView;
}

// The public payment-details-only page (the "Request payment details" link).
export interface PaymentDetailsRequestPageView {
  company: string;
  contactName: string;
}

// ---- The Xero payable contact (mirror of server/src/modules/subcontractors/subcontractorXero.routes.ts, Item 4) ----
export type XeroPlanKey = "name" | "contactPerson" | "address" | "email" | "companyNumber" | "taxNumber" | "purchasesAccountCode" | "billTerms" | "bank";
export type XeroPlanAction = "send" | "same" | "keep";

export interface XeroPlanRow {
  key: XeroPlanKey;
  label: string;
  xero: string | null;
  platform: string | null;
  action: XeroPlanAction;
}

export interface XeroContactPreview {
  mode: "create" | "update";
  contactId: string | null;
  linked: boolean;
  foundByName: boolean;
  rows: XeroPlanRow[];
  notes: string[];
  previewToken: string;
  bankSet: "current" | "previous" | "none";
  nothingToSend: boolean;
  // Why nothing may be written to this contact (another subcontractor's, or archived in Xero). Null: it may be.
  blocked: string | null;
}

export interface XeroContactPushResult {
  ok: true;
  contactId: string | null;
  created: boolean;
  sentKeys: XeroPlanKey[];
  bankSent: boolean;
  bankStored: boolean | null;
  nothingToSend: boolean;
  message?: string;
}
